import React, { useMemo } from 'react'
import FilterSelect from './FilterSelect'
import { useMyDeals } from './useMyDeals'
import {
  DEAL_ROLE_OPTIONS,
  DEAL_STAGE_OPTIONS,
  DEAL_STATUS_OPTIONS,
  DealRole,
  DealStage,
  DealStatus,
} from './filter_options' // wherever you put the constants
import { Deal, Organization } from 'types'
import { RenderPrimary, RenderType } from './ReusableUI'
import { Chip, Grid, IconButton, Typography } from '@mui/material'
import CompactList from './CompactList'
import { Box } from '@mui/system'
import CustomFiltersModal from 'components/DataGrid/components/CustomFiltersModal'
import CustomFiltersMenu from 'components/ui/custom-filters-menu-button'
import FilterListIcon from '@mui/icons-material/FilterList'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

type Props = {
  deals: Deal[]
  organizations: Organization[]
  viewUserId?: string
  endingSoonByDeal?: Map<
    string,
    {
      daysLeft: number
      count: number
    }
  >
}

export default function DealsPanel({ deals, organizations, viewUserId, endingSoonByDeal }: Props) {
  const { byOwner, byApprover, byTeam, anyMatch, allMatch, byStage, byStatus } = useMyDeals(deals, viewUserId)

  const [role, setRole] = React.useState<DealRole>('any')
  const [stage, setStage] = React.useState<DealStage | 'Any'>('Any')
  const [status, setStatus] = React.useState<DealStatus | 'Any'>('Any')
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  // Base list by role
  const base: Deal[] = React.useMemo(() => {
    switch (role) {
      case 'owner':
        return byOwner
      case 'approver':
        return byApprover
      case 'team':
        return byTeam
      case 'all':
        return allMatch
      case 'any':
      default:
        return anyMatch
    }
  }, [role, byOwner, byApprover, byTeam, anyMatch, allMatch])

  // Apply stage/status filters
  const filteredDeals = React.useMemo(() => {
    const s = stage === 'Any' ? null : stage
    const st = status === 'Any' ? null : status

    let list = base
    if (s) list = list.filter(d => (d as any)?.stage === s)
    if (st) list = list.filter(d => (d as any)?.dealStatus === st)
    return list
  }, [base, stage, status])
  const roleActive = role !== 'any' // counts 'any', 'owner', 'approver', 'team'
  const stageActive = stage !== 'Any'
  const statusActive = status !== 'Any'

  // single count (1 per group if active)
  const activeCount = useMemo(() => Number(roleActive) + Number(stageActive) + Number(statusActive), [
    roleActive,
    stageActive,
    statusActive,
  ])
  const clearFilters = () => {
    setRole('any')
    setStage('Any')
    setStatus('Any')
  }
  return (
    <>
      <CompactList
        title={
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {activeCount > 0 && (
              <IconButton onClick={clearFilters} sx={{ p: 0 }}>
                <RestartAltIcon fontSize="small" />{' '}
              </IconButton>
            )}
            <Typography variant="subtitle1">Deals</Typography>
            <Typography variant="body2" sx={{ opacity: 0.6, whiteSpace: 'nowrap' }}>
              ({filteredDeals.length})
            </Typography>
          </Box>
        }
        filter={
          <CustomFiltersMenu
            icon={<FilterListIcon sx={{ fontSize: '30px' }} />}
            onOpenCustomFilters={event => setAnchorEl(event.currentTarget)}
            count={activeCount || undefined}
            badgeColor="icon.deals"
          />
        }
        items={filteredDeals}
        primary={d => <RenderPrimary organizations={organizations} item={d} />}
        secondary={d => {
          const meta = d?.deal_uuid ? endingSoonByDeal.get(d.deal_uuid) : undefined
          const chip = meta ? (
            <Chip
              size="small"
              label={meta.count > 1 ? `ends in ${meta.daysLeft}d • ${meta.count}` : `ends in ${meta.daysLeft}d`}
              color="warning"
              variant="outlined"
              sx={{
                ml: 1,
                px: 0.5,
                height: 20,
                fontSize: 12,
                flexShrink: 0,
                color: 'text.warning',
              }}
            />
          ) : null

          return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <RenderType item={d} tag={chip} />
            </Box>
          )
        }}
      />
      <CustomFiltersModal anchorEl={anchorEl} handleCloseCustomFilters={() => setAnchorEl(null)}>
        <Grid container style={{ display: 'inline-flex', gap: '35px', flexWrap: 'nowrap' }}>
          <Grid item xs={3}>
            <FilterSelect<DealRole | 'any'>
              label="Role"
              value={role}
              onChange={setRole}
              options={DEAL_ROLE_OPTIONS}
              ariaLabel="Deal role filter"
              minWidth={115}
            />
          </Grid>
          <Grid item xs={3}>
            <FilterSelect<DealStage | 'Any'>
              label="Stage"
              value={stage}
              onChange={setStage}
              options={DEAL_STAGE_OPTIONS}
              ariaLabel="Deal stage filter"
              minWidth={115}
            />
          </Grid>
          <Grid item xs={3}>
            <FilterSelect<DealStatus | 'Any'>
              label="Status"
              value={status}
              onChange={setStatus}
              options={DEAL_STATUS_OPTIONS}
              ariaLabel="Deal status filter"
              minWidth={115}
            />
          </Grid>
        </Grid>
      </CustomFiltersModal>
    </>
  )
}
